'use client';

import { queryCollectionOptions } from '@tanstack/query-db-collection';
import { createCollection } from '@tanstack/react-db';
import {
  handleCreateGroup,
  handleDeleteGroup,
  handleDeleteItem,
  handleInsertItem,
  handleUpdateGroup,
  handleUpdateItemGroup,
  handleUpdateItemDetail,
  type CreateGroupParams,
  type InsertItemParams,
} from './mutations';
import { libraryGroupSchema, libraryItemSchema, type LibraryGroup, type LibraryItem } from './schemas';
import type { QueryClient } from '@tanstack/react-query';

async function fetchLibraryItems(): Promise<LibraryItem[]> {
  const response = await fetch('/api/library/items');
  if (!response.ok) {
    throw new Error('Failed to fetch library items');
  }
  return response.json();
}

async function fetchLibraryGroups(): Promise<LibraryGroup[]> {
  const response = await fetch('/api/library/groups');
  if (!response.ok) {
    throw new Error('Failed to fetch library groups');
  }
  return response.json();
}

function createLibraryItemCollection(userId: string, queryClient: QueryClient) {
  return createCollection(
    queryCollectionOptions({
      id: 'library-items',
      queryKey: ['library-items', userId],
      queryFn: fetchLibraryItems,
      queryClient,
      getKey: (item: LibraryItem) => item.libraryItemId,
      schema: libraryItemSchema,
      retry: 3,
      retryDelay: 1000,
      staleTime: 30000,
      onInsert: async ({ transaction }) => {
        const { modified } = transaction.mutations[0];
        const params: InsertItemParams = {
          libraryItemId: modified.libraryItemId,
          type: modified.type,
          title: modified.title,
          detail: modified.detail,
          libraryGroupId: modified.libraryGroupId ?? undefined,
        };
        await handleInsertItem(params);
      },
      onUpdate: async ({ transaction }) => {
        const { original, changes } = transaction.mutations[0];
        if (changes.libraryGroupId !== undefined) {
          await handleUpdateItemGroup(original.libraryItemId, changes.libraryGroupId ?? null);
        }
        if (changes.detail !== undefined) {
          await handleUpdateItemDetail(
            original.libraryItemId,
            changes.detail as Record<string, unknown>
          );
        }
      },
      onDelete: async ({ transaction }) => {
        const { original } = transaction.mutations[0];
        await handleDeleteItem(original.libraryItemId);
      },
    })
  );
}

function createLibraryGroupCollection(userId: string, queryClient: QueryClient) {
  return createCollection(
    queryCollectionOptions({
      id: 'library-groups',
      queryKey: ['library-groups', userId],
      queryFn: fetchLibraryGroups,
      queryClient,
      getKey: (item: LibraryGroup) => item.libraryGroupId,
      schema: libraryGroupSchema,
      retry: 3,
      retryDelay: 1000,
      staleTime: 30000,
      onInsert: async ({ transaction }) => {
        const { modified } = transaction.mutations[0];
        const params: CreateGroupParams = {
          libraryGroupId: modified.libraryGroupId,
          name: modified.name,
          description: modified.description ?? undefined,
        };
        await handleCreateGroup(params);
      },
      onUpdate: async ({ transaction }) => {
        const { original, changes } = transaction.mutations[0];
        const updates: { name?: string; description?: string | null } = {};
        if (changes.name !== undefined) updates.name = changes.name;
        if (changes.description !== undefined) updates.description = changes.description;
        if (Object.keys(updates).length > 0) {
          await handleUpdateGroup(original.libraryGroupId, updates);
        }
      },
      onDelete: async ({ transaction }) => {
        const { original } = transaction.mutations[0];
        await handleDeleteGroup(original.libraryGroupId);
      },
    })
  );
}

export type LibraryItemCollection = ReturnType<typeof createLibraryItemCollection>;
export type LibraryGroupCollection = ReturnType<typeof createLibraryGroupCollection>;

export interface LibraryCollections {
  itemCollection: LibraryItemCollection;
  groupCollection: LibraryGroupCollection;
}

export function createLibraryCollections(
  userId: string,
  queryClient: QueryClient
): LibraryCollections {
  return {
    itemCollection: createLibraryItemCollection(userId, queryClient),
    groupCollection: createLibraryGroupCollection(userId, queryClient),
  };
}
